import React, { createContext, useEffect, useReducer, useState } from "react";
import jwtDecode from "jwt-decode";
import axiosInstance from "../services/https/global";
import AppLoader from "components/shared/appLoader/AppLoader";
import { Store } from "redux/Store";
import AlertDialogSlide from "components/dialog/DialogBox";
import { clearAccessToken, setAccessToken } from "@/redux/actions/auth-actions";
import { clearActiveSession, clearPending2fa } from "@/utils/auth-session";

const initialState = {
  isAuthenticated: false,
  isInitialised: false,
  user: null,
};

const isValidToken = (accessToken) => {
  if (!accessToken) {
    return false;
  }

  const decodedToken = jwtDecode(accessToken);
  const currentTime = Date.now() / 1000;
  return decodedToken.exp > currentTime;
};

const setSession = (accessToken) => {
  if (accessToken) {
    localStorage.setItem("accessToken", accessToken);
    // axios.defaults.headers.common.Authorization = `${accessToken}`;
    // axios.defaults.headers.common['Client-Id'] = 'WAYAQUICK';
    // axios.defaults.headers.common['Client-Type'] = 'ADMIN';
  } else {
    localStorage.removeItem("accessToken");
    // axios.defaults.headers.common['Client-Type'] = 'ADMIN';
    // axios.defaults.headers.common['Client-Id'] = 'WAYAQUICK';
    // delete axios.defaults.headers.common.Authorization;
  }
};

const reducer = (state, action) => {
  switch (action.type) {
    case "INIT": {
      const { isAuthenticated, user } = action.payload;

      return {
        ...state,
        isAuthenticated,
        isInitialised: true,
        user,
      };
    }
    case "LOGIN": {
      const { user } = action.payload;

      return {
        ...state,
        isAuthenticated: true,
        user,
      };
    }
    case "LOGOUT": {
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        userDetails: null,
      };
    }

    case "FORGOT-PASSWORD": {
      const { user } = action.payload;
      return {
        ...state,
        user: user,
      };
    }

    default: {
      return { ...state };
    }
  }
};

const AuthContext = createContext({
  ...initialState,
  method: "JWT",
  login: () => Promise.resolve(),
  logout: () => {},
  forgotPassword: () => Promise.resolve(),
});

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [userDetails, setuserDetails] = useState(state?.user);
  const [userPermissions, setUserPermissions] = useState([]);
  const { REACT_APP_BASE_URL: baseUrl } = process.env;
  const [dialogData, setDialogData] = React.useState({ user: {}, open: false });

  const login = async (payload) => {
    // const response = await axiosInstance.post(`${baseUrl}/api/v1/auth/login`, payload);
    // const {
    //   data: { token, user, roles },
    // } = response.data;
    const res = await fetch(`${baseUrl}/api/v1/auth/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "client-type": "ADMIN", // Keeping lowercase as per our last discussion
        "client-id": "WAYAQUICK", // Keeping lowercase as per our last discussion
      },
      body: JSON.stringify(payload),
    });
    const responseData = await res.json();
    const {
      data: { token, user, roles },
    } = responseData;
    const decodedToken = jwtDecode(token);
    // console.log('Decoded Token:', decodedToken);
    // console.log(roles)
    // console.log(user)
    let idx = payload?.emailOrPhoneNumber.indexOf("@wayapaychat.com");

    if (!user?.isAdmin || idx < 0) {
      setDialogData({ user: user, open: true });
      return;
    }
    Store.dispatch(setAccessToken(token));
  };

  const verifyOtp = async (payload) => {
    const response = await axiosInstance.post(
      `${baseUrl}/api/v1/auth/verify-otp`,
      payload,
    );
    return response;
  };

  const forgotPassword = async (payload) => {
    const response = await axiosInstance.get(
      `${baseUrl}/api/v1/password/change-password/byEmail?email=${payload.email}`,
    );
    dispatch({
      type: "FORGOT-PASSWORD",
      payload,
    });
    return response;
  };

  const changePassword = async (payload) => {
    const response = await axiosInstance.get(
      `${baseUrl}/api/v1/change-password`,
    );
    dispatch({
      type: "CHANGE-PASSWORD",
      payload,
    });
    return response;
  };

  const logout = () => {
    // FALLBACK (remove once the Express proxy drops Max-Age on the refreshToken
    // cookie): clear the active-session marker so PersistLogin won't refresh.
    clearActiveSession();
    clearPending2fa();
    setSession(null);
    dispatch({ type: "LOGOUT" });
  };

  useEffect(() => {
    (async () => {
      try {
        const accessToken = Store.getState().auth.accessToken;

        if (accessToken && isValidToken(accessToken)) {
          dispatch({
            type: "INIT",
            payload: {
              isAuthenticated: true,
              user: null,
            },
          });
        } else {
          Store.dispatch(clearAccessToken());
          dispatch({
            type: "INIT",
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: "INIT",
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    })();
  }, []);

  if (!state.isInitialised) {
    return <AppLoader />;
  }
  const { user, open } = dialogData;
  return (
    <>
      {open && (
        <AlertDialogSlide
          open={open}
          user={user}
          handleClose={() =>
            setDialogData((data) => ({ ...data, open: false }))
          }
        />
      )}
      <AuthContext.Provider
        value={{
          ...state,
          method: "JWT",
          login,
          logout,
          forgotPassword,
          changePassword,
          verifyOtp,
          userDetails,
          setuserDetails,
          userPermissions,
          setUserPermissions,
        }}
      >
        {children}
      </AuthContext.Provider>
    </>
  );
};

export default AuthContext;
